/* Análise → Monte seu gráfico: o usuário escolhe métrica, agrupamento, divisão, filtros e tipo. */

import { api } from '../../core/api';
import { cat, state } from '../../core/estado';
import { esc, nf } from '../../core/formato';
import type { Agregado, ConfigGrafico, GraficoSalvo } from '../../tipos/api';
import { carregar } from '../../ui/carregar';
import { loading } from '../../ui/componentes';
import { ic, setHtml, valorDe } from '../../ui/dom';
import { filtrosBar } from '../../ui/filtros';
import { graficoPersonalizado } from '../../ui/graficoPersonalizado';
import { toast } from '../../ui/toast';
import { render } from '../../roteador';
import type { Pagina } from '../pagina';
import { paramsDoGrafico, SUGESTOES, tituloAutomatico } from './graficoComum';

const LIMITES = [5, 8, 12, 20, 30];

function select(chave: string, rotulo: string, opcoes: { id: string; nome: string }[], valor: string, dica = ''): string {
  return `<div class="field"><label for="g_${chave}">${rotulo}</label>
    <select class="inp" id="g_${chave}" data-muda="cfg" data-chave="${chave}">${opcoes.map((o) =>
      `<option value="${esc(o.id)}" ${o.id === valor ? 'selected' : ''}>${esc(o.nome)}</option>`).join('')}</select>
    ${dica ? `<div class="hint">${dica}</div>` : ''}</div>`;
}

function configHtml(c: ConfigGrafico): string {
  const o = cat().graficos;
  const outras = o.dimensoes.filter((d) => d.id !== c.dimensao);
  return `
    ${select('metrica', 'O que medir', o.metricas.map((m) => ({ id: m.id, nome: `${m.nome} (${m.unidade})` })), c.metrica)}
    ${select('dimensao', 'Agrupar por', o.dimensoes, c.dimensao, c.dimensao === 'material' ? 'Considera só os lançamentos de bens e serviços comprados.' : '')}
    ${c.tipo === 'pizza' ? '' : select('dimensao2', 'Dividir cada grupo por (opcional)', [{ id: '', nome: 'Não dividir' }, ...outras], c.dimensao2 || '')}
    <div class="grid g2" style="gap:0 14px">
      ${select('escopo', 'Escopo', [{ id: '', nome: 'Todos' }, { id: '1', nome: 'Escopo 1' }, { id: '2', nome: 'Escopo 2' }, { id: '3', nome: 'Escopo 3' }], c.filtros.escopo || '')}
      ${select('limite', 'Mostrar até', LIMITES.map((n) => ({ id: String(n), nome: `${n} grupos` })), String(c.limite), c.dimensao === 'mes' ? 'Por mês, mostra os 12 meses.' : 'Os demais somam em "Outros".')}
    </div>
    <div class="field"><label>Tipo de gráfico</label>
      <div class="seg" style="display:flex;flex-wrap:wrap">${o.tipos.map((t) =>
        `<button class="${t.id === c.tipo ? 'on' : ''}" data-clique="tipo" data-tipo="${t.id}">${esc(t.nome)}</button>`).join('')}</div></div>`;
}

export const montarGrafico: Pagina = {
  render() {
    const c = state.grafico;
    return `
  <h1 class="page">Monte seu gráfico</h1>
  <p class="sub">Escolha o que medir e como agrupar. O gráfico se atualiza na hora; salve os que quiser acompanhar em <a class="btn-plain" style="font-weight:700;text-decoration:underline" href="#/analise/graficos">Meus gráficos</a>.</p>
  ${filtrosBar()}
  <div class="row wrap" style="gap:8px;margin:-6px 0 20px"><span style="font-weight:600;font-size:13px;color:var(--muted)">Sugestões:</span>
    ${SUGESTOES.map((s, i) => `<button class="chip" data-clique="sugestao" data-i="${i}">${esc(s.nome)}</button>`).join('')}</div>
  <div class="grid" style="grid-template-columns:minmax(0,340px) minmax(0,1fr);align-items:start">
    <div class="card">${configHtml(c)}</div>
    <div class="card">
      <div class="between wrap" style="margin-bottom:14px"><h2 class="sec" style="margin:0">${esc(tituloAutomatico(c))}</h2>
        <span class="pill grey" id="grafResumo">…</span></div>
      <div id="grafPrev">${loading('Montando gráfico…')}</div>
      <div class="row wrap no-print" style="margin-top:22px;gap:10px;align-items:flex-end">
        <div class="field" style="flex:1;min-width:240px;margin:0"><label for="grafTitulo">Título para salvar</label>
          <input class="inp" id="grafTitulo" maxlength="120" value="${esc(state.graficoTitulo || tituloAutomatico(c))}"></div>
        <button class="btn btn-primary" data-clique="salvar">${ic('i-save')} Salvar em Meus gráficos</button>
      </div>
    </div>
  </div>`;
  },

  carregar(tk) {
    carregar(tk, [{
      id: 'grafPrev',
      busca: () => api<Agregado>('GET', '/agregado', { params: paramsDoGrafico(state.grafico) }),
      html: (a: Agregado) => {
        setHtml('grafResumo', `${nf(a.total, a.unidade === 'lançamentos' ? 0 : 2)} ${esc(a.unidade)} · ${nf(a.lancamentos, 0)} lançamento(s)`);
        return graficoPersonalizado(a);
      },
    }]);
  },

  acoes: {
    cfg(el) {
      const c = state.grafico;
      const v = (el as HTMLSelectElement).value;
      const chave = el.dataset.chave;
      if (chave === 'metrica') c.metrica = v;
      if (chave === 'dimensao') {
        c.dimensao = v;
        if (c.dimensao2 === v) c.dimensao2 = null;
      }
      if (chave === 'dimensao2') c.dimensao2 = v || null;
      if (chave === 'escopo') c.filtros = { ...c.filtros, escopo: v || undefined };
      if (chave === 'limite') c.limite = Number(v);
      state.graficoTitulo = '';
      render();
    },

    tipo(el) {
      const c = state.grafico;
      c.tipo = el.dataset.tipo as ConfigGrafico['tipo'];
      if (c.tipo === 'pizza') c.dimensao2 = null;
      render();
    },

    sugestao(el) {
      const s = SUGESTOES[Number(el.dataset.i)];
      state.grafico = structuredClone(s.config);
      state.graficoTitulo = s.nome;
      render();
    },

    async salvar(el) {
      const titulo = valorDe('grafTitulo');
      if (!titulo) return toast('Dê um título ao gráfico.');
      el.classList.add('dis');
      try {
        await api<GraficoSalvo>('POST', '/graficos', { body: { titulo, config: state.grafico } });
        toast(`"${titulo}" salvo em Meus gráficos.`);
      } catch (e) {
        toast((e as Error).message || 'Não foi possível salvar o gráfico.');
      } finally {
        el.classList.remove('dis');
      }
    },
  },
};
